import { useMemo, useState } from 'react';
import { PageHeader, Section } from '@/components/ui/index.js';
import {
  DISCIPLINE_META,
  FEEDBACK,
  ITEMS,
  LANE_KIND,
  type Discipline,
  type Priority,
  type RoadmapItem,
} from '@/data/roadmap.js';

/**
 * 活跃项目看板：只展示尚未完成的 D1–D9，完成历史留在 Git 与发行说明。
 */

const ORDER: readonly Discipline[] = [
  'product',
  'engineering',
  'growth-ops',
  'business',
  'design',
  'qa',
  'legal',
  'infra',
];

const PRIORITY_LABEL: Record<Priority, string> = {
  P0: '尽快',
  P1: '这周',
  P2: '有空',
  P3: '可选',
};

function prioRank(p: Priority | undefined): number {
  return p === undefined ? 9 : Number(p.slice(1));
}

function statusRank(s: RoadmapItem['status']): number {
  if (s === 'in_progress') return 0;
  if (s === 'todo' || s === 'decision' || s === 'blocked') return 1;
  return 2; // done
}

/** P0 焦点芯片(顶部跨部门红条用)。 */
function FocalChip({ item }: { item: RoadmapItem }): JSX.Element {
  const meta = DISCIPLINE_META[item.category];
  return (
    <div className="flex items-baseline gap-1.5 rounded-md border border-rose-500/30 bg-rose-500/15 px-2.5 py-1.5">
      <span className="text-[10px] font-medium uppercase tracking-wide" style={{ color: meta.color }}>
        {meta.label}
      </span>
      <span className="text-[12px] leading-tight text-rose-100">{item.title}</span>
    </div>
  );
}

interface LaneProps {
  d: Discipline;
  items: readonly RoadmapItem[];
}

/** 部门卡:图标 + 名 + 横切标 + 计数;内含 todo/in_progress/done 列表。 */
function Lane({ d, items }: LaneProps): JSX.Element {
  const meta = DISCIPLINE_META[d];
  const kind = LANE_KIND[d];
  const todo = items.filter((i) => i.status === 'todo' || i.status === 'decision' || i.status === 'blocked');
  const doing = items.filter((i) => i.status === 'in_progress');
  const done = items.filter((i) => i.status === 'done');

  // 卡内显示顺序: 进行 → 待办(按优先级) → 已完成(最近优先, 最多 3 条)
  const sortedTodo = [...todo].sort((a, b) => prioRank(a.priority) - prioRank(b.priority));
  const recentDone = [...done]
    .sort((a, b) => (b.shippedAt ?? '').localeCompare(a.shippedAt ?? ''))
    .slice(0, 3);

  const sumTodo = todo.length;
  const sumDoing = doing.length;
  const sumDone = done.length;

  return (
    <div
      className="rounded-xl border border-white/[0.11] bg-[#212d42] p-[14px] shadow-[inset_0_1px_0_rgba(255,255,255,0.07)]"
    >
      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <span
            className="grid h-7 w-7 shrink-0 place-items-center rounded-lg text-[15px] font-semibold ring-1 ring-white/10"
            style={{ background: `${meta.color}33`, color: meta.color }}
            aria-hidden="true"
          >
            {meta.icon}
          </span>
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <span className="text-[13.5px] font-semibold text-white">{meta.label}</span>
              <span
                className={`rounded px-1 py-px text-[9px] font-medium ${
                  kind === '横切'
                    ? 'bg-violet-500/15 text-violet-300'
                    : 'bg-shark-500/15 text-shark-300'
                }`}
              >
                {kind === '横切' ? '横切门' : '建制线'}
              </span>
            </div>
            <p className="truncate text-[11px] text-slate-400">{meta.blurb}</p>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-0.5 text-[11px] tabular-nums">
          <span className="text-slate-500">{sumTodo}</span>
          <span className="text-slate-700">/</span>
          <span className="text-amber-400">{sumDoing}</span>
          <span className="text-slate-700">/</span>
          <span className="text-emerald-400">{sumDone}</span>
        </div>
      </div>

      <div className="space-y-1 border-t border-white/[0.06] pt-2">
        {sumTodo + sumDoing === 0 && recentDone.length === 0 && (
          <p className="py-1 text-[11.5px] text-slate-500">这条线暂无项</p>
        )}
        {doing.map((it) => (
          <Row key={it.id} item={it} dot="amber" />
        ))}
        {sortedTodo.map((it) => (
          <Row key={it.id} item={it} dot={it.priority === 'P0' ? 'rose' : it.owner === 'you' ? 'amber' : 'slate'} />
        ))}
        {recentDone.length > 0 && (
          <>
            {(sumTodo + sumDoing > 0) && <div className="h-0.5" />}
            {recentDone.map((it) => (
              <Row key={it.id} item={it} dot="emerald" muted />
            ))}
            {done.length > recentDone.length && (
              <p className="pl-3 pt-0.5 text-[10.5px] text-slate-600">还有 {done.length - recentDone.length} 项已完成</p>
            )}
          </>
        )}
      </div>
    </div>
  );
}

type Dot = 'rose' | 'amber' | 'slate' | 'emerald';
const DOT_BG: Record<Dot, string> = {
  rose: 'bg-rose-500',
  amber: 'bg-amber-400',
  slate: 'bg-slate-500',
  emerald: 'bg-emerald-500/70',
};

function Row({ item, dot, muted }: { item: RoadmapItem; dot: Dot; muted?: boolean }): JSX.Element {
  const hint = item.priority !== undefined ? PRIORITY_LABEL[item.priority] : null;
  return (
    <div className={`flex items-baseline gap-2 px-1 py-0.5 text-[12.5px] ${muted === true ? 'opacity-60' : ''}`}>
      <span className={`mt-1 h-1.5 w-1.5 shrink-0 rounded-full ${DOT_BG[dot]}`} aria-hidden="true" />
      <span
        className={`min-w-0 flex-1 ${
          muted === true ? 'text-slate-500 line-through decoration-slate-700' : 'text-slate-100'
        }`}
      >
        {item.title}
      </span>
      {item.owner === 'you' && muted !== true && (
        <span className="shrink-0 text-[10px] text-amber-400/80">你</span>
      )}
      {item.owner === 'partner' && muted !== true && (
        <span className="shrink-0 text-[10px] text-violet-400/80">合伙人</span>
      )}
      {hint !== null && muted !== true && (
        <span className="shrink-0 text-[10px] text-slate-500">{hint}</span>
      )}
    </div>
  );
}

export function RoadmapPage(): JSX.Element {
  const [showFeedback, setShowFeedback] = useState(true);

  const byLane = useMemo(() => {
    const m = new Map<Discipline, RoadmapItem[]>();
    for (const d of ORDER) m.set(d, []);
    for (const it of ITEMS) {
      m.get(it.category)?.push(it);
    }
    return m;
  }, []);

  const focal = useMemo(() => {
    return ITEMS.filter((i) => i.priority === 'P0' && i.status !== 'done').sort(
      (a, b) => statusRank(a.status) - statusRank(b.status),
    );
  }, []);

  const total = ITEMS.length;
  const doneCount = ITEMS.filter((i) => i.status === 'done').length;
  const donePct = total > 0 ? Math.round((doneCount / total) * 100) : 0;

  return (
    <div className="space-y-6">
      <PageHeader
        title="项目看板"
        eyebrow="ROADMAP · BY DEPARTMENT"
        subtitle={`8 条责任线 · ${total} 项活跃决策 · ${donePct}% 完成 · 权威数据 admin/src/data/roadmap.ts`}
      />

      {/* 跨部门 P0 焦点 —— 卡上线的红线 */}
      {focal.length > 0 && (
        <div className="rounded-xl border border-rose-500/35 bg-[#1c1117] p-3.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]">
          <p className="mb-2 flex items-center gap-1.5 text-[11px] font-medium text-rose-200">
            <span className="text-rose-400">⚑</span>
            跨部门 P0 焦点 · {focal.length} 件（卡上线的）
          </p>
          <div className="flex flex-wrap gap-1.5">
            {focal.map((it) => (
              <FocalChip key={it.id} item={it} />
            ))}
          </div>
        </div>
      )}

      {/* 8 条部门泳道 —— 建制在前 4, 横切在后 4 */}
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        {ORDER.map((d) => (
          <Lane key={d} d={d} items={byLane.get(d) ?? []} />
        ))}
      </div>

      {/* 付费用户反馈 —— 产品方向金矿, 可折叠 */}
      <Section muted>
        <button
          type="button"
          onClick={() => setShowFeedback((v) => !v)}
          aria-expanded={showFeedback}
          className="flex w-full items-center gap-2 text-sm text-slate-300 transition-colors hover:text-slate-100"
        >
          <span className="w-3 shrink-0 text-slate-500">{showFeedback ? '▾' : '▸'}</span>
          <span className="font-medium">付费用户反馈 · {FEEDBACK.length} 条</span>
          <span className="ml-auto text-[11px] text-slate-500">产品方向金矿</span>
        </button>
        {showFeedback && (
          <div className="mt-4 space-y-3">
            {FEEDBACK.map((fb) => (
              <div key={fb.id} className="rounded-xl border border-violet-800/40 bg-violet-900/10 p-4">
                <div className="mb-2 flex items-center gap-2 text-xs text-slate-400">
                  <span className="font-medium text-violet-300">{fb.from}</span>
                  <span>·</span>
                  <span>{fb.date}</span>
                </div>
                <p className="mb-2 text-sm italic leading-relaxed text-slate-300">「{fb.raw}」</p>
                <p className="text-sm text-slate-100">
                  <span className="text-violet-300">→ </span>
                  {fb.takeaway}
                </p>
              </div>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}
